import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { createPaymentType, updatePaymentType, type PaymentType } from '@/api/payment-types'
import { Tag, Loader2 } from 'lucide-react'

interface Props {
  open: boolean
  type?: PaymentType | null
  onClose: () => void
  onSaved: (t: PaymentType) => void
}

export function PaymentTypeDialog({ open, type, onClose, onSaved }: Props) {
  const [name, setName]               = useState('')
  const [price, setPrice]             = useState(0)
  const [description, setDescription] = useState('')
  const [saving, setSaving]           = useState(false)

  useEffect(() => {
    if (type) {
      setName(type.name)
      setPrice(Number(type.price))
      setDescription(type.description ?? '')
    } else {
      setName('')
      setPrice(0)
      setDescription('')
    }
  }, [type, open])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || price < 0) return
    setSaving(true)
    try {
      let saved: PaymentType
      if (type) {
        const r = await updatePaymentType(type.id, { name, price, description: description || undefined })
        saved = r.payment_type
      } else {
        const r = await createPaymentType({ name, price, description: description || undefined })
        saved = r.payment_type
      }
      toast.success(type ? 'Producto actualizado' : 'Producto creado')
      onSaved(saved)
      onClose()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const isEdit = Boolean(type)

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-surface border-border text-text-primary max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
              <Tag className="w-4 h-4 text-accent" />
            </div>
            <DialogTitle className="text-base font-bold">
              {isEdit ? 'Editar producto o servicio' : 'Nuevo producto o servicio'}
            </DialogTitle>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-3">
          {/* Nombre */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={100}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ej: Sesión de masaje, Tiquete diario…"
              className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all"
            />
          </div>

          {/* Precio */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Precio <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center bg-background border border-border rounded-xl px-4 py-2.5 gap-2 focus-within:ring-2 focus-within:ring-accent/30 focus-within:border-accent/50 transition-all">
              <span className="text-text-muted text-sm font-semibold">$</span>
              <input
                type="number"
                required
                min={0}
                step={100}
                value={price}
                onChange={e => setPrice(Number(e.target.value))}
                className="flex-1 bg-transparent text-text-primary text-sm font-bold focus:outline-none"
              />
              <span className="text-text-muted text-xs font-medium">COP</span>
            </div>
            {price > 0 && (
              <p className="text-accent text-xs font-semibold">
                ${Number(price).toLocaleString('es-CO')} COP
              </p>
            )}
          </div>

          {/* Descripción */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Descripción <span className="text-text-muted font-normal normal-case">(opcional)</span>
            </label>
            <input
              type="text"
              maxLength={200}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Breve descripción del producto o servicio"
              className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-accent text-background text-sm font-bold rounded-xl hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear producto'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
